// The conversational presentation-PLAN workspace. The presenter and Eddi build a
// detailed, time-boxed delivery plan over an adaptive chat: Eddi proposes focus
// areas (toggle chips), asks questions, may request extra reference material
// (upload mid-flow), and keeps a live draft outline. When ready, a finalize pass
// produces the structured plan — sections with markdown bodies + figures + Q&A +
// delivery tips — which the presenter can edit per-section or expand into a deck.
//
// Inline/conversational (mirrors the "Ask the expert" pattern), not a run. The
// long-poll only runs while the final plan is generating.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  adjustPresentationPlanSection,
  finalizePresentationPlan,
  getPresentationPlan,
  PLAN_IN_FLIGHT,
  respondPresentationPlan,
  startPresentationPlan,
  type PlanSection,
  type PresentationPlanRead,
  type PresentationPlanTurn,
} from "@/api/presentation-plan";
import { uploadProjectFile } from "@/api/projects";
import { generatePresentation } from "@/api/presentation";
import { ApiError } from "@/api/client";
import { generationErrorMessage } from "@/api/llm-access";
import { Alert, Button, Spinner } from "@/components/ui";
import { MathText } from "@/components/math-text";
import { MarkdownLite } from "../markdown-lite";

export function PresentationPlanPage() {
  const { projectId = "" } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["presentation-plan", projectId],
    queryFn: () => getPresentationPlan(projectId),
    // Only poll while the final plan is being generated.
    refetchInterval: (q) =>
      q.state.data && PLAN_IN_FLIGHT.includes(q.state.data.status) ? 2500 : false,
    retry: false,
  });

  const notFound = query.error instanceof ApiError && query.error.status === 404;
  const plan = query.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["presentation-plan", projectId] });

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <Link to={`/app/projects/${projectId}`} className="text-sm text-ink-faint hover:text-ink">
          ← {t("Back to project")}
        </Link>
        {plan && plan.status === "complete" && (
          <span className="text-xs text-ink-faint">
            {t("{{n}} min plan", { n: plan.plan_json?.total_minutes ?? plan.duration_minutes })}
          </span>
        )}
      </div>

      <h1 className="text-xl font-semibold text-ink">{t("Plan a presentation")}</h1>

      {query.isLoading && (
        <div className="flex items-center gap-2 text-ink-soft">
          <Spinner /> {t("Loading…")}
        </div>
      )}

      {(notFound || !plan) && !query.isLoading && (
        <PlanKickoff projectId={projectId} onStarted={refresh} />
      )}

      {plan && (plan.status === "planning" || plan.status === "failed") && (
        <PlanConversation projectId={projectId} plan={plan} onChanged={refresh} />
      )}

      {plan && plan.status === "finalizing" && (
        <div className="card flex items-center gap-3 text-ink-soft">
          <Spinner /> {t("Writing your detailed plan… this takes a moment.")}
        </div>
      )}

      {plan && plan.status === "complete" && plan.plan_json && (
        <PlanResult projectId={projectId} plan={plan} onChanged={refresh} />
      )}
    </div>
  );
}

// ── kickoff: duration + optional focus → start the conversation ──────────────
// A talk can be any length the backend accepts (1–180 min). Empty/NaN/out-of-range
// input coerces to a sane value so "Start planning" never posts an invalid duration.
function clampMinutes(m: number): number {
  if (!Number.isFinite(m) || m < 1) return 15;
  return Math.min(180, Math.round(m));
}

function PlanKickoff({ projectId, onStarted }: { projectId: string; onStarted: () => void }) {
  const { t } = useTranslation();
  const [minutes, setMinutes] = useState(15);
  const [focus, setFocus] = useState("");

  const start = useMutation({
    mutationFn: () =>
      startPresentationPlan(projectId, {
        duration_minutes: clampMinutes(minutes),
        focus: focus.trim() || undefined,
      }),
    onSuccess: onStarted,
  });

  return (
    <section className="card space-y-4">
      <p className="text-sm text-ink-soft">
        {t("Tell me how long the talk is and, if you like, what you want to focus on. I'll read your material, propose focus areas, and we'll shape the plan together.")}
      </p>
      <div className="space-y-2">
        <label className="text-sm font-medium text-ink">{t("Presentation length")}</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={180}
            step={5}
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            onBlur={() => setMinutes((m) => clampMinutes(m))}
            className="w-24 rounded-lg border border-black/10 px-3 py-2 text-sm tabular-nums"
            aria-label={t("Presentation length in minutes")}
          />
          <span className="text-sm text-ink-soft">{t("min")}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[15, 30, 45, 60, 90].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMinutes(m)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                minutes === m
                  ? "bg-brand-500 text-white"
                  : "bg-black/5 text-ink-soft hover:bg-black/10"
              }`}
            >
              {m} {t("min")}
            </button>
          ))}
        </div>
      </div>
      <textarea
        value={focus}
        onChange={(e) => setFocus(e.target.value)}
        rows={3}
        placeholder={t("Focus or context (optional) — audience, angle, what matters most…")}
        className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm"
      />
      {start.error && (
        <Alert tone="error">
          {generationErrorMessage(start.error, t, t("Couldn't start planning."))}
        </Alert>
      )}
      <Button onClick={() => start.mutate()} loading={start.isPending}>
        {t("Start planning")}
      </Button>
    </section>
  );
}

// ── the co-creation conversation ─────────────────────────────────────────────
function PlanConversation({
  projectId,
  plan,
  onChanged,
}: {
  projectId: string;
  plan: PresentationPlanRead;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [selected, setSelected] = useState<string[]>(plan.focus_areas ?? []);

  const lastAssistant = useMemo(
    () => [...plan.conversation].reverse().find((tn) => tn.role === "assistant"),
    [plan.conversation],
  );
  const proposed = lastAssistant?.proposed_focus_areas ?? [];
  const readyToFinalize = !!lastAssistant?.ready_to_finalize;
  const chips = useMemo(
    () => Array.from(new Set([...selected, ...proposed])),
    [selected, proposed],
  );

  const respond = useMutation({
    mutationFn: (body: { message?: string; finish_now?: boolean }) =>
      respondPresentationPlan(projectId, {
        message: body.message,
        selected_focus_areas: selected,
        finish_now: body.finish_now,
      }),
    onSuccess: () => {
      setDraft("");
      onChanged();
    },
  });

  const finalize = useMutation({
    mutationFn: () => finalizePresentationPlan(projectId, selected),
    onSuccess: onChanged,
  });

  const toggle = (area: string) =>
    setSelected((s) => (s.includes(area) ? s.filter((a) => a !== area) : [...s, area]));

  const send = () => {
    const m = draft.trim();
    if (!m || respond.isPending) return;
    respond.mutate({ message: m });
  };

  return (
    <div className="space-y-4">
      {/* transcript */}
      <section className="card space-y-4">
        {plan.conversation.map((turn) => (
          <TurnView key={turn.id} turn={turn} />
        ))}
        {respond.isPending && (
          <div className="flex items-center gap-2 text-sm text-ink-faint">
            <Spinner /> {t("Eddi is thinking…")}
          </div>
        )}
      </section>

      {/* focus-area chips */}
      {chips.length > 0 && (
        <section className="card space-y-2">
          <h3 className="text-sm font-semibold text-ink">{t("Focus areas")}</h3>
          <p className="text-xs text-ink-faint">{t("Tap the ones you want the talk to center on.")}</p>
          <div className="flex flex-wrap gap-2">
            {chips.map((area) => {
              const on = selected.includes(area);
              return (
                <button
                  key={area}
                  type="button"
                  onClick={() => toggle(area)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    on
                      ? "border-brand-400 bg-brand-100 text-brand-700"
                      : "border-black/10 text-ink-soft hover:border-brand-300"
                  }`}
                >
                  {on ? "✓ " : ""}
                  <MathText text={area} />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* material requests */}
      {(lastAssistant?.material_requests?.length ?? 0) > 0 && (
        <section className="card space-y-3">
          <h3 className="text-sm font-semibold text-ink">{t("Material that would help")}</h3>
          {lastAssistant!.material_requests!.map((req, i) => (
            <MaterialRequestCard
              key={i}
              projectId={projectId}
              title={req.title}
              why={req.why}
              onUploaded={(name) => respond.mutate({ message: t('I added "{{name}}".', { name }) })}
              busy={respond.isPending}
            />
          ))}
        </section>
      )}

      {/* draft outline */}
      {(plan.draft_outline?.length ?? 0) > 0 && (
        <section className="card space-y-2">
          <h3 className="text-sm font-semibold text-ink">{t("Draft outline")}</h3>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-soft">
            {plan.draft_outline.map((s, i) => (
              <li key={i}>
                <span className="font-medium text-ink">{s.title}</span>
                {s.gist ? <span className="text-ink-faint"> — <MathText text={s.gist} /></span> : null}
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* composer + finalize */}
      <section className="card space-y-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send();
          }}
          rows={2}
          placeholder={t("Reply, answer a question, or tell me what to change…")}
          className="w-full resize-y rounded-md border border-black/10 px-3 py-2 text-sm"
          disabled={respond.isPending}
        />
        {(respond.error || finalize.error) && (
          <Alert tone="error">
            {generationErrorMessage(respond.error ?? finalize.error, t, t("Something went wrong."))}
          </Alert>
        )}
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" onClick={send} disabled={!draft.trim() || respond.isPending}>
            {t("Send")}
          </Button>
          <Button
            onClick={() => finalize.mutate()}
            loading={finalize.isPending}
            disabled={respond.isPending}
          >
            {readyToFinalize ? t("Create the plan") : t("Finalize now")}
          </Button>
        </div>
      </section>
    </div>
  );
}

function TurnView({ turn }: { turn: PresentationPlanTurn }) {
  const { t } = useTranslation();
  const isUser = turn.role === "user";
  return (
    <div className={isUser ? "text-right" : ""}>
      <div
        className={`inline-block max-w-[90%] rounded-2xl px-4 py-2 text-sm ${
          isUser ? "bg-brand-100 text-ink" : "bg-brand-50/60 text-ink-soft"
        }`}
      >
        <div className="mb-0.5 text-xs font-semibold text-ink-faint">
          {isUser ? t("You") : "Eddi"}
        </div>
        <MarkdownLite text={turn.text} />
        {!isUser && (turn.questions?.length ?? 0) > 0 && (
          <ul className="mt-2 list-disc space-y-1 pl-4 text-ink">
            {turn.questions!.map((q, i) => (
              <li key={i}><MathText text={q} /></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MaterialRequestCard({
  projectId,
  title,
  why,
  onUploaded,
  busy,
}: {
  projectId: string;
  title: string;
  why?: string;
  onUploaded: (name: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onPick = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadProjectFile(projectId, file, file.name, "info");
      setDone(true);
      onUploaded(file.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-black/10 bg-white p-3">
      <p className="text-sm font-medium text-ink"><MathText text={title} /></p>
      {why && <p className="text-xs text-ink-faint"><MathText text={why} /></p>}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
      <div className="mt-2 flex items-center gap-3">
        <label className={`cursor-pointer text-xs font-medium text-brand-700 ${uploading || busy ? "opacity-50" : ""}`}>
          {done ? t("✓ Added — add another?") : uploading ? t("Uploading…") : t("Upload this")}
          <input
            type="file"
            className="hidden"
            disabled={uploading || busy}
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </label>
        <Link to={`/app/projects/${projectId}/mark`} className="text-xs text-ink-faint hover:text-ink">
          {t("Mark it first →")}
        </Link>
      </div>
    </div>
  );
}

// ── the finalized plan ───────────────────────────────────────────────────────
function PlanResult({
  projectId,
  plan,
  onChanged,
}: {
  projectId: string;
  plan: PresentationPlanRead;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = plan.plan_json!;
  const [expanding, setExpanding] = useState(false);

  // Bridge: reuse the one-shot deck generator, seeded with the plan's focus.
  const expandToDeck = async () => {
    setExpanding(true);
    try {
      const brief = [data.title, ...(plan.focus_areas ?? [])].filter(Boolean).join(" — ");
      await generatePresentation(projectId, brief ? { brief } : {});
      navigate(`/app/projects/${projectId}/presentation`);
    } catch {
      setExpanding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-ink"><MathText text={data.title} /></h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={expandToDeck} loading={expanding}>
            {t("Expand into a slide deck")}
          </Button>
        </div>
      </div>

      {data.sections.map((section, i) => (
        <SectionCard
          key={i}
          projectId={projectId}
          index={i}
          section={section}
          onChanged={onChanged}
        />
      ))}

      {(data.anticipated_qa?.length ?? 0) > 0 && (
        <section className="card space-y-2">
          <h3 className="text-base font-semibold text-ink">{t("Anticipated questions")}</h3>
          {data.anticipated_qa!.map((qa, i) => (
            <div key={i} className="text-sm">
              <p className="font-medium text-ink"><MathText text={qa.question} /></p>
              {qa.answer && <p className="text-ink-soft"><MathText text={qa.answer} /></p>}
            </div>
          ))}
        </section>
      )}

      {(data.overall_delivery_tips?.length ?? 0) > 0 && (
        <section className="card space-y-2">
          <h3 className="text-base font-semibold text-ink">{t("Delivery tips")}</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-soft">
            {data.overall_delivery_tips!.map((tip, i) => (
              <li key={i}><MathText text={tip} /></li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function SectionCard({
  projectId,
  index,
  section,
  onChanged,
}: {
  projectId: string;
  index: number;
  section: PlanSection;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [directive, setDirective] = useState("");

  const adjust = useMutation({
    mutationFn: () => adjustPresentationPlanSection(projectId, index, { directive: directive.trim() }),
    onSuccess: () => {
      setDirective("");
      setAdjustOpen(false);
      onChanged();
    },
  });

  const inlineFigs = (section.figures ?? []).filter((f) => f.show_inline && f.url);
  const refFigs = (section.figures ?? []).filter((f) => !f.show_inline);

  return (
    <section className="card space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-ink">
          <span className="mr-2 rounded bg-brand-100 px-1.5 py-0.5 text-xs tabular-nums text-brand-700">
            {section.minutes} {t("min")}
          </span>
          <MathText text={section.title} />
        </h3>
        <button
          type="button"
          onClick={() => setAdjustOpen((v) => !v)}
          className="shrink-0 text-xs text-ink-faint hover:text-ink"
        >
          {t("Adjust")}
        </button>
      </div>

      {section.body_markdown && <MarkdownLite text={section.body_markdown} />}

      {inlineFigs.map((f, i) => (
        <figure key={i} className="space-y-1">
          <img
            src={f.url!}
            alt={f.caption ?? ""}
            className="w-full rounded-md border border-black/10 bg-white"
          />
          {f.caption && <figcaption className="text-xs text-ink-faint">{f.caption}</figcaption>}
        </figure>
      ))}

      {refFigs.length > 0 && (
        <p className="text-xs text-ink-faint">
          {t("Reference")}: {refFigs.map((f) => f.caption || f.key).join("; ")}
        </p>
      )}

      {adjustOpen && (
        <div className="space-y-2 rounded-lg border border-black/10 bg-brand-50/40 p-3">
          <textarea
            value={directive}
            onChange={(e) => setDirective(e.target.value)}
            rows={2}
            placeholder={t("How should this section change? (e.g. add a worked example, cut the history)")}
            className="w-full resize-y rounded-md border border-black/10 px-3 py-2 text-sm"
            disabled={adjust.isPending}
          />
          {adjust.error && (
            <p className="text-xs text-rose-600">
              {generationErrorMessage(adjust.error, t, t("Couldn't adjust the section."))}
            </p>
          )}
          <div className="flex justify-end">
            <Button
              onClick={() => adjust.mutate()}
              loading={adjust.isPending}
              disabled={!directive.trim()}
            >
              {t("Apply")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
